import {
  Archive,
  ArchiveRestore,
  ArrowRight,
  Briefcase,
  Check,
  Clock,
  Ellipsis,
  RotateCcw,
  SkipForward,
  UserPlus,
  Users,
} from "lucide-react"
import * as React from "react"

import { Button } from "@/components/ui/button"
import { FormField } from "@/components/ui/form-field"
import { IconButton } from "@/components/ui/icon-button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Sheet, SheetContent } from "@/components/ui/sheet"

import { decideRouting, firstPassReviewerId, secondPassCandidates } from "../routing-positions"
import { RelativeTime, TrailEntry } from "./routing-trail"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"

import {
  ROLE_LABEL,
  STAGE_LABEL,
  STAGE_ORDER,
  type Review,
  type ReviewStage,
  type Role,
  type UserId,
} from "../data/review-data"

/**
 * People & routing side sheet.
 *
 * The pipeline strip up top locates the review in the three-stage
 * backend workflow (initial → secondary → done). Archived is orthogonal
 * — a boolean that removes the review from working queues but preserves
 * the last stage — so it is surfaced as a separate badge, not a fourth
 * cell in the strip.
 *
 * Every hand-off appends one AssignmentEvent server-side. The History
 * timeline reads those verbatim so a finding is defensible six months
 * later: not just "moved to secondary" but "reassigned from Nadia to
 * Marisol · needs a formulation call".
 *
 * Archive and Skip-secondary sit inside a More menu because they are the
 * two actions with no cheap undo. Return-to-pool stays on the surface
 * because reversing it is just picking the review back up.
 */
export interface PeopleRoutingSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  review: Review
  colleagues: Array<{ userId: UserId; name: string; role: Role }>
  /** Who is signed in. Null while the session resolves; routing stays refused. */
  actor: { id: UserId; role: Role } | null
  onAssign: (userId: UserId, name: string, role: Role, note: string | null) => void
  onReturnToPool: () => void
  onAdvance: (secondaryReviewerId: UserId | null) => void
  onSkipSecondPass: () => void
  onArchive: () => void
  onUnarchive: () => void
}

export function PeopleRoutingSheet({
  open,
  onOpenChange,
  review,
  colleagues,
  actor,
  onAssign,
  onReturnToPool,
  onAdvance,
  onSkipSecondPass,
  onArchive,
  onUnarchive,
}: PeopleRoutingSheetProps): JSX.Element {
  const [pickedId, setPickedId] = React.useState<UserId | "">("")
  const [secondPassId, setSecondPassId] = React.useState<UserId | "">("")
  const [note, setNote] = React.useState("")

  const picked = colleagues.find((c) => c.userId === pickedId) ?? null
  const currentIndex = STAGE_ORDER.indexOf(review.assignment.currentStage)
  const nextStage = STAGE_ORDER[currentIndex + 1] ?? null

  /*
     Only on the way into the second pass. Everywhere else the field has no
     meaning: `secondary → done` closes the review, and the backend ignores it.

     Left empty the review goes to the engagement's Primary Client Contact,
     which is the ordinary path. Naming someone is for the two cases the
     backend otherwise refuses with no way forward — no Primary Client Contact
     on the engagement, or that person did the first pass themselves.
  */
  const choosesSecondPass = nextStage === "secondary"

  const holderId = review.assignment.assignee?.userId ?? null

  /*
     Decided here as well as on the server, so a control that cannot succeed
     says why instead of failing on click. The server is still the guard —
     these sentences are its own, and refusing here changes nothing it allows.
  */
  const handOff = decideRouting(actor, holderId, "hand_off")
  const throwback = decideRouting(actor, holderId, "throwback")
  const advance = decideRouting(
    actor,
    holderId,
    choosesSecondPass ? "advance_first_pass" : "sign_off_second_pass",
  )

  /* One line per distinct reason: the three actions often share one. */
  const refusals = [...new Set([handOff.refusal, throwback.refusal, advance.refusal])].filter(
    (r): r is string => r !== null,
  )

  /* Permitted against the holder's position — the trail records it as one. */
  const overriding =
    handOff.positionOverride || throwback.positionOverride || advance.positionOverride

  /* The first-pass reviewer is not offered their own second pass. */
  const firstPass = firstPassReviewerId({ assigneeId: holderId, ownerId: null }, actor?.id ?? null)
  const secondPassPeople = choosesSecondPass ? secondPassCandidates(colleagues, firstPass) : []

  const submit = () => {
    if (!picked) return
    onAssign(picked.userId, picked.name, picked.role, note.trim() || null)
    setPickedId("")
    setNote("")
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        size="md"
        title="People & routing"
        description="Who is on this review, and where it goes next."
        maximizable
      >
        {/* Sheet body doesn't scroll — the History timeline scrolls inside
            its own section. Anything else that grows past the viewport (the
            side-docked variant on a short window) still gets a scrollbar
            from the SheetContent parent. */}
        <div className="@container flex h-full flex-col">
          <StageStrip
            currentIndex={currentIndex}
            archived={review.assignment.archived}
            secondarySkipped={review.assignment.secondarySkipped}
          />

          {/* Two column stacks at @2xl: short cards (Currently-with, Client)
            stack on the left, long cards (Hand-it-to, History) stack on the
            right. Splitting into two independent column stacks — instead of
            one grid across all four sections — is what stops the short left
            column from bottoming out with white space while History keeps
            going on the right. */}
          <div className="min-h-0 flex-1 @2xl:grid @2xl:grid-cols-2 @2xl:divide-x @2xl:divide-border-soft">
            <div className="flex flex-col divide-y divide-border-soft">
              <Section
                eyebrow="Currently with"
                icon={<Users className="size-3" aria-hidden="true" />}
              >
                {review.assignment.assignee ? (
                  <AssigneeCard
                    name={review.assignment.assignee.name}
                    role={review.assignment.assignee.role}
                    sinceISO={review.assignment.assignee.sinceISO}
                  />
                ) : (
                  <UnassignedCard />
                )}
              </Section>

              <Section eyebrow="Client" icon={<Briefcase className="size-3" aria-hidden="true" />}>
                {review.client ? (
                  <div className="rounded-[12px] border border-border bg-surface p-3.5 shadow-e1">
                    <div className="flex items-start gap-3">
                      <span
                        aria-hidden="true"
                        className="grid size-9 shrink-0 place-items-center rounded-[8px] bg-print-tint font-id text-[13px] font-semibold uppercase text-print ring-1 ring-inset ring-print/15"
                      >
                        {review.client.clientName.slice(0, 2)}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-body font-semibold text-text">
                          {review.client.clientName}
                        </p>
                        <p className="mt-0.5 text-caption text-text-3">
                          {review.client.memberCount}{" "}
                          {review.client.memberCount === 1 ? "person" : "people"} can see this
                          review.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-[12px] border border-dashed border-border bg-surface-sunken/50 p-3.5">
                    <div className="flex items-start gap-3">
                      <span
                        aria-hidden="true"
                        className="grid size-9 shrink-0 place-items-center rounded-[8px] border border-dashed border-border-strong bg-surface text-text-3"
                      >
                        <Briefcase className="size-4" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-body-sm font-semibold text-text">No client attached</p>
                        <p className="mt-0.5 text-caption leading-[1.5] text-text-3">
                          Internal work — findings stay with the review team. Attach an engagement
                          on the Clients screen when it should be shared.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </Section>
            </div>

            <div className="flex min-h-0 flex-col divide-y divide-border-soft">
              <Section
                eyebrow="Hand it to"
                icon={<UserPlus className="size-3" aria-hidden="true" />}
              >
                <div className="rounded-[12px] border border-border bg-surface p-3.5 shadow-e1">
                  <div className="grid gap-3">
                    <FormField label="Colleague">
                      <Select value={pickedId} onValueChange={(v) => setPickedId(v as UserId | "")}>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose a colleague" />
                        </SelectTrigger>
                        <SelectContent>
                          {colleagues.map((c) => (
                            <SelectItem
                              key={c.userId}
                              value={c.userId}
                              description={ROLE_LABEL[c.role]}
                            >
                              {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormField>
                    {choosesSecondPass && (
                      <FormField
                        label="Second pass by"
                        hint="Leave empty to send it to the client's primary contact."
                      >
                        <Select
                          value={secondPassId}
                          onValueChange={(v) => setSecondPassId(v as UserId)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="The client's primary contact" />
                          </SelectTrigger>
                          <SelectContent>
                            {secondPassPeople.map((c) => (
                              <SelectItem
                                key={c.userId}
                                value={c.userId}
                                description={ROLE_LABEL[c.role]}
                              >
                                {c.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormField>
                    )}
                    <FormField
                      label="Note"
                      hint="Recorded on the trail — the next person reads this first."
                    >
                      <Textarea
                        minRows={3}
                        value={note}
                        onChange={(e) => setNote(e.currentTarget.value)}
                        placeholder="Why it is moving. Optional."
                      />
                    </FormField>
                    <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                      {picked ? (
                        <Button
                          variant="primary"
                          size="sm"
                          leadingIcon={<UserPlus />}
                          onClick={submit}
                          disabled={!handOff.allowed}
                        >
                          Hand it to {picked.name.split(" ")[0]}
                        </Button>
                      ) : (
                        <Button
                          variant="primary"
                          size="sm"
                          trailingIcon={<ArrowRight />}
                          onClick={() => {
                            onAdvance(secondPassId || null)
                            setSecondPassId("")
                          }}
                          disabled={!nextStage || review.assignment.archived || !advance.allowed}
                        >
                          {nextStage ? `Advance to ${STAGE_LABEL[nextStage]}` : "Already done"}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="default"
                        leadingIcon={<RotateCcw />}
                        onClick={onReturnToPool}
                        disabled={!review.assignment.assignee || !throwback.allowed}
                      >
                        Return to pool
                      </Button>
                      <span className="flex-1" />
                      <MoreMenu
                        currentStage={review.assignment.currentStage}
                        archived={review.assignment.archived}
                        onSkipSecondPass={onSkipSecondPass}
                        onArchive={onArchive}
                        onUnarchive={onUnarchive}
                      />
                    </div>
                    {/* Why a control is unavailable, in the server's own words,
                        before it is pressed rather than after it fails. */}
                    {refusals.map((refusal) => (
                      <p key={refusal} className="text-caption leading-normal text-text-3">
                        {refusal}
                      </p>
                    ))}
                    {overriding && (
                      <p className="text-caption leading-normal text-print">
                        You do not hold this review — what you change here is recorded on the trail
                        as an override.
                      </p>
                    )}
                  </div>
                </div>
              </Section>

              <Section
                eyebrow="History"
                // History fills whatever height remains in the right column and
                // scrolls its own list. Routing controls above stay put; the sheet
                // body itself doesn't scroll.
                className="min-h-0 flex-1"
                bodyClassName="overflow-y-auto pr-1"
              >
                {review.assignment.events.length === 0 ? (
                  <p className="rounded-[10px] border border-dashed border-border-soft px-3 py-4 text-center text-body-sm text-text-3">
                    Nothing recorded yet.
                  </p>
                ) : (
                  <ol className="relative space-y-0 pl-1">
                    {review.assignment.events
                      .slice()
                      .reverse()
                      .map((e, i, arr) => (
                        <TrailEntry
                          key={`${e.at}-${e.action}-${i}`}
                          event={e}
                          isFirst={i === 0}
                          isLast={i === arr.length - 1}
                        />
                      ))}
                  </ol>
                )}
              </Section>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}

/**
 * Section container. Every section is separated by a hairline; the eyebrow
 * uses the mono id typeface for the small-caps feel the redesign relies on.
 */
function Section({
  eyebrow,
  icon,
  children,
  className,
  bodyClassName,
}: {
  eyebrow: string
  icon?: React.ReactNode
  children: React.ReactNode
  /** Extra classes on the <section>. Used to let History flex-fill the column. */
  className?: string
  /** Extra classes on the body wrapper. Used to let History scroll internally. */
  bodyClassName?: string
}): JSX.Element {
  return (
    <section
      className={cn(
        "flex flex-col border-b border-border-soft px-5 py-4 last:border-b-0",
        className,
      )}
    >
      <h3 className="mb-3 flex items-center gap-1.5 font-id text-[10px] uppercase tracking-[0.1em] text-text-3">
        {icon && <span className="opacity-70">{icon}</span>}
        {eyebrow}
      </h3>
      <div className={cn("min-h-0", bodyClassName)}>{children}</div>
    </section>
  )
}

/**
 * The three-stage pipeline strip. Backend workflow is initial → secondary
 * → done; archived is a boolean, not a fourth stage, and renders as a
 * banner over the strip. "Secondary skipped" replaces the middle cell's
 * check with a skip glyph so the trail is legible from the strip alone.
 */
function StageStrip({
  currentIndex,
  archived,
  secondarySkipped,
}: {
  currentIndex: number
  archived: boolean
  secondarySkipped: boolean
}): JSX.Element {
  return (
    <div className="border-b border-border-soft bg-surface-sunken/30 px-5 py-4">
      <div className="mb-2 flex items-center justify-between">
        <span className="font-id text-[10px] uppercase tracking-[0.1em] text-text-3">Pipeline</span>
        <span className="font-id text-[10px] tabular-nums text-text-3">
          Stage {currentIndex + 1} / {STAGE_ORDER.length}
        </span>
      </div>
      <ol
        aria-label="Review pipeline"
        className="grid overflow-hidden rounded-[10px] border border-border bg-surface shadow-e1"
        style={{ gridTemplateColumns: `repeat(${STAGE_ORDER.length}, minmax(0, 1fr))` }}
      >
        {STAGE_ORDER.map((s, i) => {
          const past = i < currentIndex
          const active = i === currentIndex
          const skipped = secondarySkipped && s === "secondary"
          return (
            <li
              key={s}
              aria-current={active ? "step" : undefined}
              className={cn(
                "relative min-w-0 border-r border-border-soft px-2 py-2.5 last:border-r-0 transition-colors",
                past && "bg-master-tint/60",
                active && "bg-surface shadow-[inset_0_-3px_0_var(--brand)]",
                skipped && "bg-print-tint/40",
              )}
            >
              <div className="flex items-center gap-1.5">
                {skipped ? (
                  <span
                    aria-hidden="true"
                    className="grid size-3 shrink-0 place-items-center rounded-full bg-print"
                  >
                    <SkipForward className="size-2 text-white" strokeWidth={3} />
                  </span>
                ) : past ? (
                  <span
                    aria-hidden="true"
                    className="grid size-3 shrink-0 place-items-center rounded-full bg-master"
                  >
                    <Check className="size-2 text-white" strokeWidth={3} />
                  </span>
                ) : active ? (
                  <span
                    aria-hidden="true"
                    className="size-2 shrink-0 rounded-full bg-brand ring-2 ring-brand/20"
                  />
                ) : (
                  <span
                    aria-hidden="true"
                    className="size-2 shrink-0 rounded-full border border-border-strong"
                  />
                )}
                <span
                  className={cn(
                    "truncate text-[11px] font-semibold leading-none",
                    active && "text-text",
                    past && "text-master",
                    skipped && "text-print",
                    !active && !past && !skipped && "text-text-disabled",
                  )}
                >
                  {STAGE_LABEL[s]}
                </span>
              </div>
            </li>
          )
        })}
      </ol>
      {archived && (
        <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-surface-sunken px-2.5 py-1 font-id text-[10px] font-medium uppercase tracking-[0.08em] text-text-2">
          <Archive className="size-3" aria-hidden="true" />
          Archived — hidden from the working queue
        </p>
      )}
    </div>
  )
}

/**
 * Assignee card. Role-tinted avatar with a live "since" timer so glancing
 * at the sheet answers "who and how long" without a second click.
 */
function AssigneeCard({
  name,
  role,
  sinceISO,
}: {
  name: string
  role: Role
  sinceISO: string
}): JSX.Element {
  return (
    <div className="rounded-[12px] border border-border bg-surface p-3.5 shadow-e1">
      <div className="flex items-center gap-3">
        <RoleAvatar role={role} name={name} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-body font-semibold text-text">{name}</p>
          <p className="mt-0.5 flex items-center gap-1.5 truncate text-caption text-text-3">
            <span>{ROLE_LABEL[role]}</span>
            <span aria-hidden="true" className="text-text-3/60">
              ·
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock className="size-3" aria-hidden="true" />
              <RelativeTime iso={sinceISO} />
            </span>
          </p>
        </div>
      </div>
    </div>
  )
}

function UnassignedCard(): JSX.Element {
  return (
    <div className="rounded-[12px] border border-dashed border-border bg-surface-sunken/50 p-3.5">
      <div className="flex items-center gap-3">
        <span
          aria-hidden="true"
          className="grid size-9 shrink-0 place-items-center rounded-full border border-dashed border-border-strong bg-surface"
        >
          <Users className="size-4 text-text-3" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-body font-semibold text-text">Unassigned</p>
          <p className="mt-0.5 text-caption text-text-3">
            Waiting in the pool — nobody has picked it up.
          </p>
        </div>
      </div>
    </div>
  )
}

const ROLE_BG: Record<Role, string> = {
  admin: "bg-brand",
  reviewer: "bg-info",
  sme: "bg-people",
  viewer: "bg-text-2",
  client: "bg-print",
}

/**
 * Role-tinted avatar. Uses two-letter initials for legibility at this size.
 * Colour maps to the backend Role enum so the same colour always means the
 * same role across the app — reviewer info-blue, sme people-violet, and so on.
 */
function RoleAvatar({ role, name }: { role: Role; name?: string }): JSX.Element {
  const initials = name
    ? name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0])
        .join("")
        .toUpperCase()
    : role[0]?.toUpperCase()
  return (
    <span
      aria-hidden="true"
      className={cn(
        "grid size-9 shrink-0 place-items-center rounded-full font-id text-[13px] font-semibold uppercase text-white ring-2 ring-surface",
        ROLE_BG[role],
      )}
    >
      {initials}
    </span>
  )
}

function MoreMenu({
  currentStage,
  archived,
  onSkipSecondPass,
  onArchive,
  onUnarchive,
}: {
  currentStage: ReviewStage
  archived: boolean
  onSkipSecondPass: () => void
  onArchive: () => void
  onUnarchive: () => void
}): JSX.Element {
  const [open, setOpen] = React.useState(false)
  const canSkipSecondary = currentStage === "initial" && !archived
  /* Only a review already returned to the client can be put away. */
  const canArchive = currentStage === "done"
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <IconButton size="sm" variant="ghost" label="More routing actions" icon={<Ellipsis />} />
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={6} className="w-64 p-1">
        <MenuItem
          icon={<SkipForward className="size-3.5" aria-hidden="true" />}
          label="Skip secondary review"
          hint="Goes straight to done. Recorded on the trail."
          disabled={!canSkipSecondary}
          onSelect={() => {
            setOpen(false)
            onSkipSecondPass()
          }}
        />
        {/* One or the other, never both: the two are opposite ends of the same
            switch, and every routing action refuses an archived review until
            it is flipped back. */}
        {archived ? (
          <MenuItem
            icon={<ArchiveRestore className="size-3.5" aria-hidden="true" />}
            label="Unarchive this review"
            hint="Returns it to the working queue so it can be routed again."
            onSelect={() => {
              setOpen(false)
              onUnarchive()
            }}
          />
        ) : (
          <MenuItem
            icon={<Archive className="size-3.5" aria-hidden="true" />}
            label="Archive this review"
            hint={
              canArchive
                ? "Hidden from the working queue. The trail is kept."
                : "Only a review returned to the client can be archived."
            }
            destructive
            disabled={!canArchive}
            onSelect={() => {
              setOpen(false)
              onArchive()
            }}
          />
        )}
      </PopoverContent>
    </Popover>
  )
}

function MenuItem({
  icon,
  label,
  hint,
  destructive,
  disabled,
  onSelect,
}: {
  icon: React.ReactNode
  label: string
  hint: string
  destructive?: boolean
  disabled?: boolean
  onSelect: () => void
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      className={cn(
        "flex w-full items-start gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors duration-120 ease-out",
        destructive ? "text-err hover:bg-err-tint" : "text-text hover:bg-surface-sunken",
        "disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]",
      )}
    >
      <span className="mt-[3px] shrink-0 opacity-80">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-body-sm font-medium leading-tight">{label}</span>
        <span className="mt-0.5 block text-micro leading-normal text-text-3">{hint}</span>
      </span>
    </button>
  )
}
